import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';
import { allDemos, youtubeVideos } from '../data/mock';
import AudioPlayer from '../components/ui/AudioPlayer';

const categories = [
  { id: 'all', label: 'All Demos' },
  { id: 'commercial', label: 'Commercials' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'ivr', label: 'IVR & Telephone' },
  { id: 'promo', label: 'Promo & Imaging' },
  { id: 'documentary', label: 'Documentary' },
  { id: 'pidgin', label: 'Nigerian Pidgin' },
];

const DemosPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const getFilteredDemos = () => {
    if (activeCategory === 'all') {
      return Object.entries(allDemos).flatMap(([category, demos]) =>
        demos.filter(demo => demo.audioUrl).map((demo) => ({ ...demo, category }))
      );
    }
    return (allDemos[activeCategory] || []).filter(demo => demo.audioUrl).map((demo) => ({
      ...demo,
      category: activeCategory,
    }));
  };

  const getCategoryLabel = (categoryId) => {
    const labels = {
      commercial: 'Commercial',
      corporate: 'Corporate',
      ivr: 'IVR',
      promo: 'Promo',
      documentary: 'Documentary',
      pidgin: 'Nigerian Pidgin',
    };
    return labels[categoryId] || categoryId;
  };

  const filteredDemos = getFilteredDemos();

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#0D0D0D] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Portfolio</span>
            <h1 className="heading-hero mt-4 mb-6">
              Voice <span className="text-[#C9A962]">Demos</span>
            </h1>
            <p className="body-large">
              Explore Joy Ogbekene's full range of voiceover work across multiple formats 
              and delivery styles. These samples highlight versatility, tone control, and 
              cultural adaptability.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-40 bg-[#0A0A0A]/95 backdrop-blur-md border-y border-[#1A1A1A] py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  activeCategory === category.id
                    ? 'bg-[#C9A962] text-[#0D0D0D]'
                    : 'bg-[#161616] text-[#B8B8B8] border border-[#2A2A2A] hover:border-[#C9A962]/50 hover:text-white'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Audio Demos Grid */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {activeCategory === 'all' ? (
            // Show categorized sections
            <div className="space-y-16">
              {Object.entries(allDemos).map(([categoryKey, demos]) => {
                const audioDemos = demos.filter(demo => demo.audioUrl);
                if (audioDemos.length === 0) return null;
                
                return (
                  <div key={categoryKey}>
                    <h2 className="heading-2 mb-8 capitalize">
                      {getCategoryLabel(categoryKey)}
                      <span className="text-[#707070] text-base font-normal ml-3">
                        ({audioDemos.length} samples)
                      </span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {audioDemos.map((demo) => (
                        <AudioPlayer
                          key={`${categoryKey}-${demo.id}`}
                          title={demo.title}
                          category={getCategoryLabel(categoryKey)}
                          duration={demo.duration}
                          description={demo.tone}
                          audioUrl={demo.audioUrl}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Show filtered demos
            <div>
              <h2 className="heading-2 mb-8">
                {categories.find((c) => c.id === activeCategory)?.label}
                <span className="text-[#707070] text-base font-normal ml-3">
                  ({filteredDemos.length} samples)
                </span>
              </h2>
              {filteredDemos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredDemos.map((demo, index) => (
                    <AudioPlayer
                      key={`${demo.category}-${demo.id}-${index}`}
                      title={demo.title}
                      category={getCategoryLabel(demo.category)}
                      duration={demo.duration}
                      description={demo.tone}
                      audioUrl={demo.audioUrl}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[#707070]">Audio demos for this category coming soon. Check out the video demos below!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Video Demos Section */}
      <section className="py-16 md:py-24 bg-[#0D0D0D] border-t border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Video Samples</span>
            <h2 className="heading-1 mt-4">Video Demos</h2>
            <p className="body-large mt-4 max-w-2xl mx-auto">
              Watch Joy's voiceover work in context across commercials, documentaries, and corporate productions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((video) => (
              <div
                key={video.id}
                className="bg-[#161616] border border-[#1A1A1A] rounded-2xl overflow-hidden hover:border-[#C9A962]/30 transition-colors group"
              >
                {/* YouTube Embed */}
                <div className="aspect-video relative">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
                {/* Video Info */}
                <div className="p-5">
                  <h3 className="text-white font-medium mb-1 group-hover:text-[#C9A962] transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-[#707070] text-sm">{video.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0A0A0A] border-t border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="heading-1 mb-6">Like What You Hear?</h2>
          <p className="body-large mb-12 max-w-xl mx-auto">
            Get in touch to discuss your project or request a custom audition.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A962] text-[#0D0D0D] font-medium rounded-full hover:bg-[#E5D4A1] transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,169,98,0.3)]"
            >
              Request Custom Audition
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#2A2A2A] text-white font-medium rounded-full hover:border-[#C9A962] hover:text-[#C9A962] transition-all duration-300"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DemosPage;
